<div class="p-6">
  <!-- File picker -->
  @if (batchUploadService.state().status === "idle") {
    <div class="flex flex-col gap-4">
      <input
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.png,.jpg,.jpeg"
        (change)="onFilesSelected($event)"
        #fileInput
        class="hidden"
      />

      <button
        pButton
        type="button"
        icon="pi pi-upload"
        class="p-button-outlined font-bold"
        (click)="fileInput.click()"
      >
        {{ "calculator.selectFiles" | transloco }}
      </button>

      <p class="text-paragraph-s-reg text-greyscale-700">
        {{
          "calculator.bulkUploadPackLimit"
            | transloco: { limit: batchUploadService.defaultMaxFiles() }
        }}
      </p>

      @if (batchUploadService.packLimitationWarning()) {
        <div
          class="mb-4 flex flex-row items-center justify-between gap-8 rounded-md border border-warning-200 bg-warning-50 p-1 px-2 text-paragraph-s-reg text-warning-700"
        >
          <span class="flex-1">
            {{
              "pack.tripLimit.BulkUploadWaring"
                | transloco
                  : {
                      maxFiles: batchUploadService.maxFiles(),
                      defaultMaxFiles: batchUploadService.defaultMaxFiles(),
                    }
            }}</span
          >
          <p-button
            [label]="'pack.tripLimit.purchaseMoreTrip' | transloco"
            [text]="true"
            severity="warn"
            (onClick)="openModal()"
          />
        </div>
      }

      <p class="mt-2 text-xs text-gray-500">
        {{ "calculator.acceptedFormats" | transloco }}
      </p>

      <!-- Selected files list -->
      @if (batchUploadService.files().length > 0) {
        <div class="mt-4">
          <div class="mb-3 flex items-center justify-between">
            <div>
              <p class="flex items-center gap-2 font-medium text-gray-700">
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                  [class.bg-primary-100]="!batchUploadService.isOverLimit()"
                  [class.text-primary-700]="!batchUploadService.isOverLimit()"
                  [class.bg-red-50]="batchUploadService.isOverLimit()"
                  [class.text-red-500]="batchUploadService.isOverLimit()"
                >
                  {{ batchUploadService.files().length }}/{{ batchUploadService.maxFiles() }}
                </span>
                {{ "calculator.filesSelected" | transloco }}
              </p>
              <p class="text-xs text-gray-500">
                {{ "calculator.totalSize" | transloco }}:
                {{ batchUploadService.totalSizeMb() }} MB
              </p>
            </div>
            <button
              pButton
              type="button"
              icon="pi pi-trash"
              class="p-button-outlined p-button-sm"
              (click)="batchUploadService.clearFiles()"
              [label]="'calculator.clearAll' | transloco"
              [attr.aria-label]="'calculator.clearAll' | transloco"
            ></button>
          </div>

          <!-- Warning banner when over limit -->
          @if (batchUploadService.isOverLimit()) {
            <div class="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
              <div class="flex items-start gap-2">
                <i class="pi pi-info-circle text-lg text-red-500"></i>
                <div class="flex-1 text-sm">
                  <p class="font-medium text-gray-800">
                    {{ "calculator.tooManyFiles" | transloco }}
                  </p>
                  <p class="mt-1 text-gray-700">
                    {{
                      "calculator.tooManyFilesDetail"
                        | transloco
                          : {
                              total: batchUploadService.files().length,
                              max: batchUploadService.maxFiles(),
                              excess: batchUploadService.excessCount(),
                            }
                    }}
                  </p>
                </div>
              </div>
            </div>
          }

          <ul class="mb-4 space-y-2">
            @for (file of batchUploadService.files(); track file.name) {
              <li
                class="flex cursor-move items-center gap-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700 transition-opacity"
                [class.opacity-50]="draggedFileIndex === $index"
                draggable="true"
                (dragstart)="onDragStart($event, $index)"
                (dragover)="onDragOver($event)"
                (drop)="onDrop($event, $index)"
                (dragend)="onDragEnd()"
              >
                <!-- Drag handle -->
                <div class="cursor-move text-gray-400 hover:text-gray-600">
                  <i class="pi pi-bars text-lg"></i>
                </div>

                <!-- File info -->
                <div class="flex flex-1 items-center gap-2">
                  @if (!batchUploadService.isFileIncluded($index)) {
                    <i class="pi pi-exclamation-circle text-red-500"></i>
                  }
                  <div class="flex-1">
                    <p class="font-medium text-gray-700">
                      {{ file.name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ (file.size / 1024 / 1024).toFixed(2) }} MB
                      @if (!batchUploadService.isFileIncluded($index)) {
                        <span class="font-medium text-red-600">
                          -
                          {{ "calculator.willNotBeUploaded" | transloco }}</span
                        >
                      }
                    </p>
                  </div>
                </div>

                <!-- Remove button -->
                <button
                  pButton
                  type="button"
                  icon="pi pi-times"
                  class="p-button-text p-button-sm p-button-rounded"
                  (click)="batchUploadService.removeFile($index)"
                  aria-label="Remove file"
                ></button>
              </li>
            }
          </ul>

          <!-- Invisible drop zone for placing files at the end -->
          <div
            class="h-12"
            (dragover)="onDragOver($event)"
            (drop)="onDropAtEnd($event)"
          ></div>

          <div class="mt-4 flex justify-end gap-3">
            <button
              pButton
              type="button"
              [label]="'common.cancel' | transloco"
              class="p-button-outlined"
              (click)="cancel()"
              [attr.aria-label]="'common.cancel' | transloco"
            ></button>
            <button
              pButton
              type="button"
              icon="pi pi-send"
              [label]="'calculator.analyze' | transloco"
              (click)="startAnalysis()"
              [disabled]="
                batchUploadService.files().length === 0 || batchUploadService.isOverLimit()
              "
              [attr.aria-label]="'calculator.analyze' | transloco"
            ></button>
          </div>
        </div>
      }
    </div>
  }

  <!-- Terminal: done / partial / error -->
  @else if (
    batchUploadService.state().status === "done" ||
    batchUploadService.state().status === "error"
  ) {
    <div class="flex flex-col items-center justify-center gap-4 text-center">
      @if (batchUploadService.state().status === "error") {
        <i class="pi pi-times-circle text-4xl text-red-500"></i>
        <p class="text-gray-700">{{ "calculator.uploadError" | transloco }}</p>
      } @else if (batchUploadService.state().failed > 0) {
        <i class="pi pi-exclamation-triangle text-4xl text-warning-500"></i>
        <p class="text-gray-700">
          {{
            "calculator.documentsPartiallyUploaded"
              | transloco
                : {
                    uploaded:
                      batchUploadService.state().total -
                      batchUploadService.state().failed,
                    failed: batchUploadService.state().failed,
                  }
          }}
        </p>
      } @else {
        <i class="pi pi-check-circle text-4xl text-primary-500"></i>
        <p class="font-medium text-gray-800">
          {{ "calculator.documentsUploaded" | transloco }}
        </p>
        <p class="text-sm text-gray-600">
          {{
            "calculator.processingTimeEstimate"
              | transloco
                : {
                    minutes: batchUploadService.estimatedMinutes(
                      batchUploadService.state().total
                    ),
                  }
          }}
        </p>
      }
      <button
        pButton
        type="button"
        [label]="'common.close' | transloco"
        (click)="close()"
        [attr.aria-label]="'common.close' | transloco"
      ></button>
    </div>
  }

  <!-- Upload in progress -->
  @else {
    <div class="flex flex-col items-center justify-center text-center">
      <p class="mb-3 font-medium text-gray-800">
        {{ "calculator.uploadInProgress" | transloco }}
      </p>
      @if (batchUploadService.state().status === "uploading") {
        <p-progressBar
          class="w-full"
          [value]="batchUploadService.uploadPercent()"
          [showValue]="false"
        />
        <p class="mt-1 text-xs text-gray-500">
          {{ batchUploadService.state().uploaded }} /
          {{ batchUploadService.state().total }}
        </p>
      } @else {
        <p-progressBar
          class="w-full"
          mode="indeterminate"
          [style]="{ height: '6px' }"
        />
      }
      <p class="mt-3 text-xs text-gray-500">
        {{ "calculator.bulkUploadBackgroundHint" | transloco }}
      </p>
    </div>
  }
</div>
